import { normalize } from "normalizr";
import { camelizeKeys, decamelizeKeys } from "humps";
import { isEmpty } from "lodash";
import { getCookie } from "./cookieUtils";

const filterParams = (params) =>
    Object.entries(params).reduce((accumulator, [key, value]) => {
        accumulator[`filter[${key}]`] = value; // eslint-disable-line no-param-reassign
        return accumulator;
    }, {});

const getNextPageUrl = (response) => {
    const link = response.meta?.pagination?.next;
    if (!link) return null;

    const nextLink = link.trim().split("/").slice(-3).join("/");
    return nextLink ? `/${nextLink}` : null;
};

const handleResponse = async (response, schema) => {
    if (response.status === 204 || response.status === 205) {
        return null;
    }

    const json = await response.json();

    if (!response.ok) {
        throw json?.errors || response.statusText;
    }

    let payload = camelizeKeys(json);
    const nextPageUrl = getNextPageUrl(payload);

    if (schema && !isEmpty(payload)) {
        payload = normalize(payload, schema);
    }

    return nextPageUrl ? { ...payload, nextPageUrl } : payload;
};

const request = async (params) => {
    const { method = "GET", url, data, filters, headers: customHeaders, schema } = params;

    const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...customHeaders,
    };

    if (method !== "GET") {
        const csrfToken =
            getCookie("XSRF-TOKEN") ||
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
        if (csrfToken) {
            headers["X-CSRF-Token"] = csrfToken;
        }
    }

    const payloadData = { ...data, ...filterParams(filters || {}) };
    let finalUrl = url;
    let body;

    if (!isEmpty(payloadData)) {
        const decamelizedData = decamelizeKeys(payloadData);
        if (["GET", "DELETE"].includes(method)) {
            const queryString = new URLSearchParams(decamelizedData).toString();
            finalUrl = `${url}?${queryString}`;
        } else {
            body = JSON.stringify(decamelizedData);
        }
    }

    const response = await fetch(finalUrl, {
        method,
        headers,
        body,
        credentials: "same-origin",
    });

    return handleResponse(response, schema);
};

export default {
    get: (params) => request({ ...params, method: "GET" }),
    post: (params) => request({ ...params, method: "POST" }),
    put: (params) => request({ ...params, method: "PUT" }),
    patch: (params) => request({ ...params, method: "PATCH" }),
    delete: (params) => request({ ...params, method: "DELETE" }),
};
